import { parseISO, format } from 'date-fns';
import { formatDecimalAsPercentage } from 'ns_libs/formatter';
import { isDebtSource } from 'utils/sourceTypeUtils';
import {
    IProFormaGeneralInfo,
    IOperatingInfo,
    IOperatingPeriodCashFlows,
    ISourcesSummary,
    ISourceType,
} from 'views/ProFormaTable/types';
import { ProFormaModelIds } from 'constants/proForma';
import { OperationsEntity } from 'views/ProFormaTable/constants';
import { IMonthlyAnalysisPeriod } from './MonthlyAnalysisTable/MonthlyAnalysisTable';

export const getMonthlyAnalysisPeriods = (
    proForma: IProFormaGeneralInfo,
    cashFlowPeriods: string[],
    operatingInfo: IOperatingInfo,
    operatingPeriodCashFlows: (IOperatingPeriodCashFlows | null)[],
    saleMonth?: number,
) => {
    if (!operatingInfo.operationBeginMonth) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            leasedPercentage: '—',
            economicOccupancyPercentage: '—',
        }];
    }

    const periods = saleMonth !== undefined ? cashFlowPeriods.slice(0, saleMonth + 1) : cashFlowPeriods;
    const cashFlows = saleMonth !== undefined ? operatingPeriodCashFlows.slice(0, saleMonth + 1) : operatingPeriodCashFlows;

    const monthlyAnalysisPeriods: IMonthlyAnalysisPeriod[] = periods.map((periodStartDate, index) => {
        let leasedPercentage = '—';
        let economicOccupancyPercentage = '—';
        if (operatingInfo.operationStartDate && periods.length === cashFlows.length) {
            const cashFlowPeriod = cashFlows[index];

            leasedPercentage = formatDecimalAsPercentage(cashFlowPeriod?.leasedPercentage || 0, 2);
            economicOccupancyPercentage = formatDecimalAsPercentage(cashFlowPeriod?.economicOccupancyPercentage || 0, 2);
        }
        return ({
            periodName: format(parseISO(periodStartDate), 'MMM yyyy'),
            periodDate: periodStartDate,
            leasedPercentage,
            economicOccupancyPercentage,
        });
    });

    return monthlyAnalysisPeriods;
};

export interface IDebtFundingWindow {
    firstDebtMonth: number | null;
    lastDebtMonth: number | null;
}

export const getDebtFundingWindow = (
    sourcesSummary: ISourcesSummary | undefined,
    sourceTypes: ISourceType[] | undefined,
): IDebtFundingWindow => {
    const sources = sourcesSummary?.sources;
    if (!sources?.length || !sourceTypes?.length) {
        return { firstDebtMonth: null, lastDebtMonth: null };
    }

    const fundedDebtMonths = sources
        .filter((source) => isDebtSource(source.sourceTypeId, sourceTypes))
        .flatMap((source) => (source.periods ?? []).reduce<number[]>((months, period, index) => {
            const amount = Number(period?.amountToFund);
            if (Number.isFinite(amount) && amount > 0) months.push(index);
            return months;
        }, []));

    if (!fundedDebtMonths.length) {
        return { firstDebtMonth: null, lastDebtMonth: null };
    }

    return {
        firstDebtMonth: Math.min(...fundedDebtMonths),
        lastDebtMonth: Math.max(...fundedDebtMonths),
    };
};

export const getStabilizedMonthMissingEntity = (proForma: IProFormaGeneralInfo, isEstimate: boolean) => {
    let missingEntity = OperationsEntity.UNIT_MIX;
    if (![ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId)) {
        if (isEstimate) {
            missingEntity = OperationsEntity.FLOOR_ALLOCATIONS;
        } else {
            missingEntity = OperationsEntity.TENANTS;
        }
    }
    return missingEntity;
};
